import * as React from 'react'
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom'

import {
  Box,
  Icon,
  Section,
  Text,
  TogglerCheckbox,
  Tooltip,
  Touchable,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  ApprovalGroupDetailsFragment,
  UpdateApprovalGroupDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { RuleCard } from 'modules/approvals/components/RuleCard'
import { List } from 'modules/approvals/components/RulesConfiguration/List'
import UpgradePlan from 'modules/approvals/components/UpgradePlan'

const DetailsPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [openTooltip, toggleTooltip] = useToggle(false)

  const { group } = useOutletContext<{ group: ApprovalGroupDetailsFragment }>()
  const [updateAutoApprove, { loading }] = useMutation(UpdateApprovalGroupDocument, {
    extractorFn: (data) => data.approvalsMutations.updateApprovalsApprovalGroup,
  })
  const [autoApproved, setAutoApproved] = React.useState(group?.autoApproved)
  const [showUpgradeBox, setShowUpdateBox] = React.useState(false)

  const handleAutoApprovedChange = async () => {
    await updateAutoApprove({
      variables: {
        id: group.id,
        autoApproved: !autoApproved,
        description: group.description,
        name: group.name,
      },
    })
    setAutoApproved(!autoApproved)
  }

  const defaultFlow = group?.flows.find((flow) => flow.default)

  const navigateToCreateRule = () => {
    navigate(
      navigation.approvals.approvalGroupsCategoryId.rulesConfigurations.new.fullPath({
        id: group.id,
        category: group.category,
      })
    )
  }

  const descriptionName = () => {
    switch (group.category) {
      case 'expenses':
        return i18n.t('settings.approvals.approval_group.description.expenses')
      case 'timeoff':
        return i18n.t('settings.approvals.approval_group.description.timeoff')
      case 'time_tracking':
        return i18n.t('settings.approvals.approval_group.description.time_tracking')
      case 'ats':
        return i18n.t('settings.approvals.approval_group.description.ats')
      case 'premium_portals':
        return i18n.t('settings.approvals.approval_group.description.premium_portals')
      case 'contracts':
        return i18n.t('settings.approvals.approval_group.description.contracts')
      case 'software':
        return i18n.t('settings.approvals.approval_group.description.software')
      case 'trainings':
        return i18n.t('settings.approvals.approval_group.description.trainings')
      case 'purchases':
        return i18n.t('settings.approvals.approval_group.description.purchases')
      default:
        return i18n.t('settings.approvals.approval_group.description.default')
    }
  }

  const title = i18n.t('settings.approvals.approval_group.details.rules_title')
  let description = i18n.t('settings.approvals.approval_group.description.label', {
    name: descriptionName(),
  })
  let action

  const { domainConfigurationRulesStatus } = group

  if (domainConfigurationRulesStatus === 'enabled') {
    action = {
      label: i18n.t('settings.approvals.approval_rule.add_rule'),
      onClick: navigateToCreateRule,
    }
    description = i18n.t('settings.approvals.approval_group.description.default_label', {
      name: descriptionName(),
    })
  }

  if (domainConfigurationRulesStatus === 'upgrade_plan') {
    action = {
      label: i18n.t('settings.approvals.approval_rule.add_rule'),
      onClick: () => setShowUpdateBox(true),
    }
  }

  return (
    <Section
      title={title}
      description={description}
      illustration="network"
      paddingX="s0"
      paddingY="s24"
      action={action}
    >
      <Outlet context={{ group }} />

      <Box paddingTop="s40">
        {showUpgradeBox && <UpgradePlan />}
        <Box
          borderRadius={{ all: 'abs008' }}
          border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
          padding="s16"
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
        >
          <Touchable onClick={handleAutoApprovedChange} disabled={loading}>
            <Box flexDirection="row" gap="s8" alignItems="center" height="s32">
              <Text size="200" weight="medium" color="grey1000">
                {i18n.t('settings.approvals.approval_group.new.auto_approval')}
              </Text>
              <Tooltip.Alt
                attachment="bottom left"
                open={openTooltip}
                toggle={toggleTooltip}
                label={i18n.t('settings.approvals.approval_group.new.auto_approval_tooltip')}
              >
                <Icon.Medium icon="info-circle-line" color="grey800" />
              </Tooltip.Alt>
            </Box>
          </Touchable>
          <TogglerCheckbox
            labelBefore
            labelWeight="medium"
            value={autoApproved}
            onChange={handleAutoApprovedChange}
            disabled={loading}
          />
        </Box>
        {!autoApproved && (
          <Box paddingTop="s16">
            <List group={group} />
            <RuleCard
              flow={defaultFlow}
              title={i18n.t('settings.approvals.defaults.flow.default_name')}
              index={group.rulesConfiguration.length + 1}
              tagLabel={i18n.t('settings.approvals.detail.default_tag')}
              canEdit
              canAddSteps
              canDeleteSteps
            />
          </Box>
        )}
      </Box>
    </Section>
  )
}

export default DetailsPage
